import { TFunction } from 'i18next';

import {
  CLUSTER_RADIUS_DEFAULT,
  CLUSTER_RADIUS_HIGH_ZOOM_DEFAULT,
  CLUSTER_RADIUS_LOW_ZOOM_DEFAULT,
  CLUSTER_RADIUS_ZOOM_BREAKPOINT_DEFAULT,
  CLUSTER_ZOOM_THRESHOLD_DEFAULT,
} from '../common/Constants';
import { mapIcons } from './mapIcons.utils';

export const USER_LAYER = 'curLocation';
export const CLUSTER_LAYER = 'clusters';
export const RESULT_LAYER = 'unclustered-point';
export const ROUTE_LAYER = 'nav-route';
export const PLACES_SOURCE = 'places';
export const GROUPED_LAYER = 'groupedLocations';

export const getDistanceString = (distance: number) => {
  const miles = distance / 1609.344;
  return `${miles?.toFixed(1)} mi`;
};

export const getStepDistanceString = (distance: number) => {
  if (distance > 0) {
    const miles = distance / 1609.344;
    return `${miles?.toFixed(1)} mi`;
  }
  return '';
};

export const getStepIcon = (modifier: string | undefined, type: string) => {
  const direction = `${type ? type.replace(/ /g, '_') : ''}${
    modifier ? `_${modifier.replace(/ /g, '_')}` : ''
  }`;
  return Object.keys(mapIcons).includes(direction) ? mapIcons[direction] : '';
};

export const getDurationString = (
  t: TFunction,
  duration: number,
  isSecondRequired: boolean = false
) => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = Math.floor(duration % 60);

  const formattedTime = [
    { time: hours, title: hours > 1 ? 'hours' : 'hour' },
    { time: minutes, title: minutes > 1 ? 'minutes' : 'minute' },
    { time: seconds, title: seconds > 1 ? 'seconds' : 'second' },
  ];

  if (!isSecondRequired) formattedTime.pop();

  return formattedTime
    .filter(({ time }) => Boolean(time))
    .map((item) => `${item.time} ${t(item.title)}`)
    .join(' ');
};

export const getNavTypeString = (navType: string) => {
  switch (navType) {
    case 'driving-traffic':
      return 'drive with traffic';
    case 'walking':
      return 'walk';
    case 'cycling':
      return 'bike ride';
    default:
      return 'drive';
  }
};

export const stringToIntegerOrDefault = (
  value: string,
  defaultValue: number
) => {
  const parsedValue = parseInt(value);
  return isNaN(parsedValue) ? defaultValue : parsedValue;
};

export const stringToFloatOrDefault = (value: string, defaultValue: number) => {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? defaultValue : parsedValue;
};

export const getClusterRadiusForZoom = (
  zoom: number,
  breakpointZoom: number,
  lowZoomRadius: number,
  highZoomRadius: number
): number => (zoom >= breakpointZoom ? highZoomRadius : lowZoomRadius);

// Debug overlay that renders the live zoom level and cluster radius values on the
// map so the clustering behaviour can be visualized while tuning the thresholds.
export const updateClusterDebugOverlay = (
  map,
  breakpointZoom: number,
  lowZoomRadius: number,
  highZoomRadius: number
) => {
  const container = map?.getContainer?.();
  if (!container) {
    return;
  }
  const OVERLAY_ID = 'cluster-debug-overlay';
  let overlay = container.querySelector(`#${OVERLAY_ID}`);
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.setAttribute('data-testid', OVERLAY_ID);
    Object.assign(overlay.style, {
      position: 'absolute',
      top: '8px',
      left: '8px',
      zIndex: '2',
      padding: '6px 10px',
      background: 'rgba(0, 0, 0, 0.72)',
      color: '#fff',
      font: '12px/1.4 monospace',
      borderRadius: '4px',
      pointerEvents: 'none',
      whiteSpace: 'pre',
    });
    container.appendChild(overlay);
  }
  const zoom = map?.getZoom?.() ?? 0;
  const appliedRadius =
    map.appliedClusterRadius ??
    getClusterRadiusForZoom(
      zoom,
      breakpointZoom,
      lowZoomRadius,
      highZoomRadius
    );
  overlay.textContent =
    `zoom: ${zoom.toFixed(2)}\n` +
    `cluster radius: ${appliedRadius}px\n` +
    `radius breakpoint zoom: ${breakpointZoom}`;
};

interface ClusterSourceManagerArgs {
  map;
  featuresAr;
  clusterMaxZoom: string;
  clusterRadius: string;
  clusterRadiusZoomBreakpoint: string;
  clusterRadiusLowZoom: string;
  clusterRadiusHighZoom: string;
  enableSyncMapPinsWithList: boolean;
  enableClusterDebugOverlay: boolean;
  removeAllMarkers: () => void;
}

// A freshly (re)added or re-clustered geojson source is not queryable until the
// map finishes loading its tiles for the current zoom, so querySourceFeatures
// returns nothing right after a zoom or rebuild. This resolves once the cluster
// source is loaded, with a timeout fallback so a missed sourcedata event can't
// permanently block updates.
export const waitForClusterSourceLoaded = (map): Promise<void> =>
  new Promise((resolve) => {
    if (
      typeof map?.on !== 'function' ||
      typeof map?.isSourceLoaded !== 'function' ||
      map.isSourceLoaded(CLUSTER_LAYER)
    ) {
      resolve();
      return;
    }
    const finish = () => {
      map.off?.('sourcedata', handleSourceData);
      clearTimeout(timeoutId);
      resolve();
    };
    const handleSourceData = (event) => {
      if (
        event?.sourceId === CLUSTER_LAYER &&
        event?.sourceDataType !== 'metadata' &&
        map.isSourceLoaded(CLUSTER_LAYER)
      ) {
        finish();
      }
    };
    const timeoutId = setTimeout(finish, 1000);
    map.on('sourcedata', handleSourceData);
  });

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// querySourceFeatures only returns features from loaded, rendered tiles, so right
// after a zoom or a source rebuild it can return an empty set even though the
// source has data. Wait for the source to load, then retry across a few render
// cycles until the clusters are queryable. Bounded so a genuinely empty viewport
// does not stall.
export const queryClusterFeaturesWhenReady = async (map): Promise<any[]> => {
  if (typeof map?.querySourceFeatures !== 'function') {
    return [];
  }
  const expectsFeatures = (map?.clusterFeatures?.length ?? 0) > 0;
  let features: any[] = [];
  for (let attempt = 0; attempt < 8; attempt++) {
    await waitForClusterSourceLoaded(map);
    features = map.querySourceFeatures(CLUSTER_LAYER) || [];
    if (!expectsFeatures || features.length > 0) {
      return features;
    }
    await delay(60);
  }
  return features;
};

// Sets up the clustered geojson source/layer and returns the handlers that keep
// the cluster radius in sync with the zoom level. The zoom-breakpoint radius is
// only applied when enableSyncMapPinsWithList is set; otherwise a static radius
// is used. Shared by the mapbox and maplibre pin loaders.
export const createClusterSourceManager = ({
  map,
  featuresAr,
  clusterMaxZoom,
  clusterRadius,
  clusterRadiusZoomBreakpoint,
  clusterRadiusLowZoom,
  clusterRadiusHighZoom,
  enableSyncMapPinsWithList,
  enableClusterDebugOverlay,
  removeAllMarkers,
}: ClusterSourceManagerArgs) => {
  const clusterMaxZoomValue = stringToIntegerOrDefault(
    clusterMaxZoom,
    CLUSTER_ZOOM_THRESHOLD_DEFAULT
  );
  const clusterRadiusValue = stringToIntegerOrDefault(
    clusterRadius,
    CLUSTER_RADIUS_DEFAULT
  );
  const clusterRadiusZoomBreakpointValue = stringToIntegerOrDefault(
    clusterRadiusZoomBreakpoint,
    CLUSTER_RADIUS_ZOOM_BREAKPOINT_DEFAULT
  );
  const clusterRadiusLowZoomValue = stringToIntegerOrDefault(
    clusterRadiusLowZoom,
    CLUSTER_RADIUS_LOW_ZOOM_DEFAULT
  );
  const clusterRadiusHighZoomValue = stringToIntegerOrDefault(
    clusterRadiusHighZoom,
    CLUSTER_RADIUS_HIGH_ZOOM_DEFAULT
  );

  // Keep the latest features on the map so the cluster source can be rebuilt
  // with a new radius when the zoom threshold is crossed.
  map.clusterFeatures = featuresAr;

  const addClusterSourceAndLayer = (radiusValue: number) => {
    map?.addSource(CLUSTER_LAYER, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: map.clusterFeatures,
      },
      cluster: true,
      clusterMaxZoom: clusterMaxZoomValue,
      clusterRadius: radiusValue,
    });

    map?.addLayer({
      id: CLUSTER_LAYER,
      type: 'symbol',
      source: CLUSTER_LAYER,
    });
  };

  const getRadiusForCurrentZoom = () =>
    enableSyncMapPinsWithList
      ? getClusterRadiusForZoom(
          map?.getZoom?.() ?? clusterMaxZoomValue,
          clusterRadiusZoomBreakpointValue,
          clusterRadiusLowZoomValue,
          clusterRadiusHighZoomValue
        )
      : clusterRadiusValue;

  const refreshClusterDebugOverlay = () => {
    if (!enableClusterDebugOverlay || !enableSyncMapPinsWithList) {
      return;
    }
    updateClusterDebugOverlay(
      map,
      clusterRadiusZoomBreakpointValue,
      clusterRadiusLowZoomValue,
      clusterRadiusHighZoomValue
    );
  };

  if (!map?.getSource(CLUSTER_LAYER)) {
    const initialRadius = getRadiusForCurrentZoom();
    addClusterSourceAndLayer(initialRadius);
    map.appliedClusterRadius = initialRadius;
  } else {
    map.getSource(CLUSTER_LAYER).setData({
      type: 'FeatureCollection',
      features: featuresAr,
    });
    if (map.appliedClusterRadius == null) {
      map.appliedClusterRadius = getRadiusForCurrentZoom();
    }
  }

  refreshClusterDebugOverlay();

  // Rebuild the cluster source with a zoom-appropriate radius. MapLibre/Mapbox
  // do not allow mutating clusterRadius on an existing source, so the source is
  // removed and re-added only when the radius actually changes.
  const reconcileClusterRadiusForZoom = () => {
    if (!enableSyncMapPinsWithList || !map?.getSource(CLUSTER_LAYER)) {
      return;
    }
    const desiredRadius = getRadiusForCurrentZoom();
    if (desiredRadius === map.appliedClusterRadius) {
      return;
    }
    map.appliedClusterRadius = desiredRadius;
    removeAllMarkers();
    if (map?.getLayer(CLUSTER_LAYER)) {
      map.removeLayer(CLUSTER_LAYER);
    }
    map.removeSource(CLUSTER_LAYER);
    addClusterSourceAndLayer(desiredRadius);
    refreshClusterDebugOverlay();
  };

  return { reconcileClusterRadiusForZoom, refreshClusterDebugOverlay };
};
